import { useEffect, useRef } from 'react';
import Hls from 'hls.js';

/**
 * Attaches a video source to a <video> element, using HLS when the source is a
 * manifest rather than a file.
 *
 * ## Why this is not just `src={url}`
 *
 * Safari plays HLS natively. Chrome and Firefox do not — pointing a `<video>` at a
 * `.m3u8` in either produces a player that loads nothing and reports no error a user
 * could act on. hls.js fills that gap by fetching the manifest itself and feeding
 * segments to the element through Media Source Extensions.
 *
 * `hls.js` was already in package.json and imported nowhere, so migrating a film to
 * Bunny Stream before this existed would have produced content that plays in Safari and
 * silently fails everywhere else — the worst kind of bug to ship, because whoever
 * tested it on a Mac would have seen it work.
 *
 * ## Why the format is passed in rather than sniffed
 *
 * The backend says `HLS` or `PROGRESSIVE`. Guessing from a `.m3u8` extension works
 * until a signed URL appends query parameters, or a CDN serves a manifest from a path
 * that does not end in `.m3u8` — at which point the guess fails in a way that looks
 * like the video being broken. The extension check remains as a fallback for callers
 * that have no format, but the explicit value wins.
 */
export type DeliveryFormat = 'HLS' | 'PROGRESSIVE';

export function isHlsSource(url: string | undefined | null, format?: DeliveryFormat | null): boolean {
  if (format) return format === 'HLS';
  if (!url) return false;
  // Fallback only. Matches before any query string so a signed manifest still counts.
  return /\.m3u8(\?|$)/i.test(url);
}

export function useHlsPlayback(
  videoRef: React.RefObject<HTMLVideoElement | null>,
  url: string | undefined | null,
  format?: DeliveryFormat | null,
  onFatalError?: (message: string) => void,
) {
  const hlsRef = useRef<Hls | null>(null);

  useEffect(() => {
    const video = videoRef.current;
    if (!video || !url) return;

    // Always tear down a previous instance first. Without this, navigating between
    // two films leaves the old one attached and buffering in the background, which
    // shows up as mysterious bandwidth use and occasionally as the wrong audio.
    if (hlsRef.current) {
      hlsRef.current.destroy();
      hlsRef.current = null;
    }

    if (!isHlsSource(url, format)) {
      video.src = url;
      return;
    }

    // Safari (and iOS, where hls.js cannot work at all because MSE is unavailable in
    // the way it needs). Native playback is also better here - hardware decoding and
    // AirPlay both work, which they do not through MSE.
    if (video.canPlayType('application/vnd.apple.mpegurl')) {
      video.src = url;
      return;
    }

    if (!Hls.isSupported()) {
      onFatalError?.('This browser cannot play this video. Please try Chrome, Firefox or Safari.');
      return;
    }

    const hls = new Hls({
      // Start conservatively and let it climb. The alternative - beginning on the
      // highest rung - is what makes a video sit on a black screen for several
      // seconds before the first frame, which viewers read as the site being slow.
      startLevel: -1,
      // Enough buffer to ride out a tunnel, not so much that seeking discards a
      // large download the viewer already paid for on mobile data.
      maxBufferLength: 30,
    });

    hls.on(Hls.Events.ERROR, (_event, data) => {
      if (!data.fatal) return;

      // Network and media errors are frequently transient — a dropped segment on a
      // train, a decoder hiccup. hls.js can recover from both, and destroying the
      // player on the first one would turn a half-second glitch into a dead page.
      switch (data.type) {
        case Hls.ErrorTypes.NETWORK_ERROR:
          hls.startLoad();
          return;
        case Hls.ErrorTypes.MEDIA_ERROR:
          hls.recoverMediaError();
          return;
        default:
          hls.destroy();
          hlsRef.current = null;
          onFatalError?.('This video could not be played. It may still be processing.');
      }
    });

    hls.loadSource(url);
    hls.attachMedia(video);
    hlsRef.current = hls;

    return () => {
      if (hlsRef.current) {
        hlsRef.current.destroy();
        hlsRef.current = null;
      }
    };
  }, [videoRef, url, format, onFatalError]);
}
